import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Container,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider
} from '@mui/material';
import {
  ExitToApp as LogoutIcon,
  AccountCircle as AccountIcon,
  Security as SecurityIcon,
  Dashboard as DashboardIcon,
  LocalShipping as MoversIcon
} from '@mui/icons-material';

// Import the revenue optimization components
import AdminOperationsDashboard from '../components/AdminOperationsDashboard.jsx';
import AdminMoverApproval from '../components/AdminMoverApproval.jsx';
import MovdLogo from '../components/MovdLogo.jsx';

const AdminPortal = () => {
  const [userData, setUserData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is authenticated and is admin
    const token = localStorage.getItem('token');
    const storedUserData = localStorage.getItem('userData');

    if (!token || !storedUserData) {
      navigate('/mover-portal');
      return;
    }

    try {
      const parsedUserData = JSON.parse(storedUserData);
      if (parsedUserData.role !== 'admin') {
        navigate('/mover-portal');
        return;
      }
      setUserData(parsedUserData);
    } catch (error) {
      console.error('Error parsing user data:', error);
      navigate('/mover-portal');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    navigate('/mover-portal');
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (!userData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading admin portal...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F4F7FB' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#fff', color: '#102A43', borderBottom: '1px solid #D9E2EC' }}>
        <Toolbar sx={{ minHeight: { xs: 64, md: 72 }, px: { xs: 2, md: 3 }, gap: 2 }}>
          <MovdLogo size="small" color="primary" sx={{ flexShrink: 0 }} onClick={() => navigate('/admin-portal')} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: '#627D98', fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase' }}>
              Admin Portal
            </Typography>
            <Typography variant="h6" component="div" sx={{ fontWeight: 850, lineHeight: 1.1, display: { xs: 'none', sm: 'block' } }}>
              MOVD Operations
            </Typography>
          </Box>
          <Button startIcon={<DashboardIcon />} onClick={() => navigate('/admin-portal')} sx={{ display: { xs: 'none', md: 'inline-flex' }, fontWeight: 700 }}>
            Dashboard
          </Button>
          <Button startIcon={<MoversIcon />} onClick={() => navigate('/admin-portal/movers')} sx={{ display: { xs: 'none', md: 'inline-flex' }, fontWeight: 700 }}>
            Movers
          </Button>
          <Typography variant="body2" sx={{ mr: 1, color: '#627D98', display: { xs: 'none', md: 'block' } }}>
            {userData.name}
          </Typography>
          <IconButton
            onClick={handleMenuOpen}
            size="small"
            sx={{ ml: 0 }}
            aria-controls={Boolean(anchorEl) ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: '#176B87', fontWeight: 800 }}>
              {userData.name?.charAt(0)}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleMenuClose}>
              <SecurityIcon sx={{ mr: 2 }} />
              Admin Access
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/admin-portal'); }}>
              <DashboardIcon sx={{ mr: 2 }} />
              Dashboard
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/admin-portal/movers'); }}>
              <AccountIcon sx={{ mr: 2 }} />
              Mover Controls
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 2 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        <Routes>
          <Route path="" element={<AdminOperationsDashboard onOpenMovers={() => navigate('/admin-portal/movers')} />} />
          <Route path="revenue-dashboard" element={<AdminOperationsDashboard onOpenMovers={() => navigate('/admin-portal/movers')} />} />
          <Route path="optimization" element={<AdminOperationsDashboard onOpenMovers={() => navigate('/admin-portal/movers')} />} />
          <Route path="leads" element={<AdminOperationsDashboard onOpenMovers={() => navigate('/admin-portal/movers')} />} />

          {/* Mover approval and commission controls */}
          <Route path="movers" element={<AdminMoverApproval />} />
          
          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/admin-portal/" replace />} />
        </Routes>
      </Container>
    </Box>
  );
};

export default AdminPortal;